import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  ErrorText,
  MessageText,
  TreemapContainer,
  TreemapRectangle,
  RectangleName,
  RectanglePercentage,
  TableContainer,
  TableHeader,
  TableHeaderText,
  TableRow,
  TableRowLeft,
  ColorIndicator,
  IndustryName,
  PercentageBadge,
  ContentContainer,
} from "./StrategyTreemap.styles";
import { isMobileDevice } from "utils/index";

const StrategyTreemap = ({ data, isLoading, error }) => {
  // 🎨 Refined blue-violet palette
  const industryColors = useMemo(
    () => [
      "#DAE8FF",
      "#90BFFF",
      "#2258F1",
      "#BDB4FE",
      "#EBE9FE",
      "#6938EF",
      "#4C3AE3",
      "#A5B4FC",
      "#D0E2FF",
      "#C3B5FF",
      "#ADC8FF",
      "#8AA4F9",
    ],
    []
  );

  const hexToRgb = (hex) => {
    if (!hex) return { r: 0, g: 0, b: 0 };
    const normalized = hex.replace("#", "");
    const bigint = parseInt(
      normalized.length === 3
        ? normalized
            .split("")
            .map((c) => c + c)
            .join("")
        : normalized,
      16
    );
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  };

  const isLightColor = (color) => {
    const { r, g, b } = hexToRgb(color);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 180;
  };

  const getTextColor = (backgroundColor) =>
    isLightColor(backgroundColor) ? "#0f172a" : "#ffffff";

  const industries = useMemo(() => {
    if (!data || typeof data !== "object") return [];
    return Object.entries(data).map(([name, val], index) => ({
      name,
      percentage: `${(val * 100).toFixed(1)}%`,
      value: val * 100,
      color: industryColors[index % industryColors.length],
    }));
  }, [data, industryColors]);

  const isMobile = isMobileDevice();
  const containerWidth = isMobile ? 300 : 600;
  const containerHeight = isMobile ? 300 : 420;

  const rectangles = useMemo(() => {
    const sorted = [...industries].sort((a, b) => b.value - a.value);
    const rects = [];
    const spacing = 4;

    const squarify = (arr, x, y, w, h) => {
      if (arr.length === 0) return;
      if (arr.length === 1) {
        rects.push({ ...arr[0], x, y, width: w, height: h });
        return;
      }
      const total = arr.reduce((s, d) => s + d.value, 0);
      const item = arr[0];
      const ratio = item.value / total;
      const horizontal = w > h;
      if (horizontal) {
        const itemWidth = w * ratio;
        rects.push({
          ...item,
          x,
          y,
          width: itemWidth - spacing,
          height: h - spacing,
        });
        squarify(arr.slice(1), x + itemWidth, y, w - itemWidth, h);
      } else {
        const itemHeight = h * ratio;
        rects.push({
          ...item,
          x,
          y,
          width: w - spacing,
          height: itemHeight - spacing,
        });
        squarify(arr.slice(1), x, y + itemHeight, w, h - itemHeight);
      }
    };

    squarify(sorted, 0, 0, containerWidth, containerHeight);
    return rects;
  }, [industries, containerWidth, containerHeight]);

  if (isLoading)
    return (
      <Card>
        <CardHeader>
          <CardTitle>Industry allocation</CardTitle>
        </CardHeader>
        <CardContent>
          <MessageText>Loading industry allocation data...</MessageText>
        </CardContent>
      </Card>
    );

  if (error)
    return (
      <Card>
        <CardHeader>
          <CardTitle>Industry allocation</CardTitle>
        </CardHeader>
        <CardContent>
          <ErrorText>
            Error loading industry allocation: {error.message || error}
          </ErrorText>
        </CardContent>
      </Card>
    );

  if (!industries.length)
    return (
      <Card>
        <CardHeader>
          <CardTitle>Industry allocation</CardTitle>
        </CardHeader>
        <CardContent>
          <MessageText>No industry allocation data available</MessageText>
        </CardContent>
      </Card>
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Industry allocation</CardTitle>
      </CardHeader>

      <CardContent>
        <ContentContainer>
          <TreemapContainer>
            {rectangles.map((rect, i) => {
              const textColor = getTextColor(rect.color);
              const fontSize = isMobile
                ? rect.width > 60
                  ? "10px"
                  : rect.width > 40
                  ? "9px"
                  : "8px"
                : rect.width > 80
                ? "11px"
                : rect.width > 50
                ? "10px"
                : "9px";

              return (
                <TreemapRectangle
                  key={`${rect.name}-${i}`}
                  $x={rect.x}
                  $y={rect.y}
                  $width={rect.width}
                  $height={rect.height}
                  $containerWidth={containerWidth}
                  $containerHeight={containerHeight}
                  $backgroundColor={rect.color}
                  title={`${rect.name}: ${rect.percentage}`}
                >
                  <RectangleName
                    title={`${rect.name}: ${rect.percentage}`}
                    $fontSize={fontSize}
                    $textColor={textColor}
                  >
                    {rect.name}
                  </RectangleName>
                  <RectanglePercentage
                    title={rect.percentage}
                    $fontSize={fontSize}
                    $textColor={textColor}
                  >
                    {rect.percentage}
                  </RectanglePercentage>
                </TreemapRectangle>
              );
            })}
          </TreemapContainer>

          <TableContainer>
            <TableHeader>
              <TableHeaderText>Industry</TableHeaderText>
              <TableHeaderText>Percentage</TableHeaderText>
            </TableHeader>
            {[...industries]
              .sort((a, b) => b.value - a.value)
              .map((ind, i) => (
                <TableRow key={`${ind.name}-${i}`}>
                  <TableRowLeft>
                    <ColorIndicator $color={ind.color} />
                    <IndustryName title={ind.name}>{ind.name}</IndustryName>
                  </TableRowLeft>
                  <PercentageBadge>{ind.percentage}</PercentageBadge>
                </TableRow>
              ))}
          </TableContainer>
        </ContentContainer>
      </CardContent>
    </Card>
  );
};

StrategyTreemap.propTypes = {
  data: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default StrategyTreemap;
